import React from "react";
import { Row, Col } from "react-bootstrap";
import {
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  Cell,
  PieLabelRenderProps,
  Sector,
} from "recharts";
import { roundFloat, wholeNumberToString } from "../../../utils/DataConversion";

export interface SliceOfPie {
  name: string;
  value: number;
  subtitle: string;
  color: string;
  order: number;
}

interface ResourcePie {
  data: SliceOfPie[];
}

export default function ResourcePie(props: ResourcePie) {
  const { data } = props;

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = (pieProps: PieLabelRenderProps) => {
    const { cx, cy, innerRadius, outerRadius, midAngle, name } = pieProps;
    // need to properly cast these items because recharts gives flexibility to types
    // todo clean this up and have variant outcomes using typechecking
    const castedRadiuses = [innerRadius as number, outerRadius as number];
    const castedPositions = [cx as number, cy as number];
    const radius =
      castedRadiuses[0] + (castedRadiuses[1] - castedRadiuses[0]) * 0.15;
    // todo clean-up midAngle availability logic
    const x = castedPositions[0] + radius * Math.cos(-midAngle! * RADIAN);
    const y = castedPositions[1] + radius * Math.sin(-midAngle! * RADIAN);

    const value = data.find((record) => record.name === name);

    return (
      <text
        style={{ fontSize: "13px", fontWeight: "bold" }}
        x={x}
        y={y}
        fill="white"
        textAnchor={x > castedPositions[0] ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${value?.value}%`}
      </text>
    );
  };

  const renderActiveShape = (props: any) => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
    } = props;
    return (
      <g>
        <text x={cx} y={cy - 10} dy={8} textAnchor="middle">
          {wholeNumberToString(
            data.find((slice) => slice.name === "total")?.value
          )}
        </text>
        <text x={cx} y={cy} dy={15} textAnchor="middle">
          Total Capacity
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col xs={12} md={4} className="mt-5 pr-0">
          {data
            .filter(
              (record) => record.name !== "total" && record.name !== "date"
            )
            .map((d, i) => (
              <div
                className="card-inner-card text-center"
                key={i}
                style={{ color: d.color }}
              >
                <p>
                  {" "}
                  <span className="h4 font-weight-bold">
                    {d.value ? roundFloat(d.value, 1) : 0.0}%
                  </span>
                  <br />
                  <span className="black"> {d.subtitle}</span>
                </p>
              </div>
            ))}
        </Col>
        <Col xs={12} md={8}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.filter(
                  (record) => record.name !== "total" && record.name !== "date"
                )}
                dataKey="value"
                paddingAngle={2}
                isAnimationActive={true}
                innerRadius={65}
                outerRadius={110}
                activeShape={renderActiveShape}
                label={(props: PieLabelRenderProps) =>
                  renderCustomizedLabel({ ...props })
                }
                labelLine={false}
                activeIndex={0}
              >
                {data
                  .filter(
                    (record) =>
                      record.name !== "total" && record.name !== "date"
                  )
                  .map((d, i) => (
                    <Cell fill={d.color} key={i} />
                  ))}
              </Pie>
              <Tooltip
                formatter={(value) => (value as number).toFixed(1) + "%"}
              />
            </PieChart>
          </ResponsiveContainer>
        </Col>
      </Row>
    </React.Fragment>
  );
}
